import React, { useMemo } from "react";
import dayjs from "dayjs";
import "./GanttView.css";

const GanttView = ({ data, type, selectedUser }) => {
  // Helper function to open Salesforce record
  const openSalesforceRecord = (itemId) => {
    if (itemId) {
      const salesforceUrl = `https://gargleinc.lightning.force.com/${itemId}`;
      window.open(salesforceUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Generate timeline (current month + next month, organized by weeks)
  const timeline = useMemo(() => {
    const weeks = [];
    const startOfCurrentMonth = dayjs().startOf("month");
    const endOfNextMonth = dayjs().add(1, "month").endOf("month");

    let currentWeekStart = startOfCurrentMonth.startOf("week");

    while (
      currentWeekStart.isBefore(endOfNextMonth) ||
      currentWeekStart.isSame(endOfNextMonth, "week")
    ) {
      const weekEnd = currentWeekStart.endOf("week");
      const days = [];

      for (let i = 0; i < 7; i++) {
        const day = currentWeekStart.add(i, "day");
        days.push(day);
      }

      weeks.push({
        weekStart: currentWeekStart,
        weekEnd: weekEnd,
        days: days,
        isCurrentMonth: currentWeekStart.month() === dayjs().month(),
        isNextMonth:
          currentWeekStart.month() === dayjs().add(1, "month").month(),
      });

      currentWeekStart = currentWeekStart.add(1, "week");
    }

    return weeks;
  }, []);

  // Helper function to check if item is completed
  const isCompleted = (item) => {
    if (type === "tickets") {
      return item.status === "Closed" || item.status === "Solved";
    } else if (type === "website") {
      return item.isCompleted || item.status === "Completed";
    } else {
      return item.status === "Completed";
    }
  };

  // Helper function to calculate item position and width
  const getItemPosition = (item) => {
    const createdDate = dayjs(item.createdDate);
    const expectedDate = dayjs(item.expectedDate);
    const timelineStart =
      timeline[0]?.weekStart || dayjs().startOf("month").startOf("week");
    const timelineEnd =
      timeline[timeline.length - 1]?.weekEnd ||
      dayjs().add(1, "month").endOf("month").endOf("week");

    // Calculate the actual duration based on the item's intended start and end dates
    const actualDurationDays = Math.max(
      1,
      expectedDate.diff(createdDate, "day") + 1
    );

    // Calculate position relative to timeline start
    const startDays = Math.max(0, createdDate.diff(timelineStart, "day"));

    // Calculate end position, but don't truncate the duration
    // If the item extends beyond the timeline, we'll clip it visually but maintain the intended width
    const endDays = startDays + actualDurationDays - 1;

    // Fixed widths: each day = 20px, each week = 140px
    const dayWidth = 20; // pixels
    const weekWidth = 140; // pixels

    // Calculate position and width in pixels
    const startPixels = startDays * dayWidth;
    const widthPixels = Math.max(dayWidth, actualDurationDays * dayWidth);

    return {
      left: `${startPixels}px`,
      width: `${widthPixels}px`,
      startDays,
      endDays,
      durationDays: actualDurationDays,
    };
  };

  // Helper function to detect collisions and calculate stack positions
  const calculateStackPositions = (items) => {
    if (!items || items.length === 0) return { items: [], containerHeight: 50 };

    // Get positions for all items
    const itemsWithPositions = items.map((item) => ({
      ...item,
      position: getItemPosition(item),
    }));

    // Sort by priority first (rocks first, then urgent, etc.), then by expected date (most recent first)
    itemsWithPositions.sort((a, b) => {
      // Priority order: ROCKS > Urgent > Medium > Normal
      const priorityOrder = { ROCKS: 4, Urgent: 3, Medium: 2, Normal: 1 };
      const aPriority = priorityOrder[a.priority] || 1;
      const bPriority = priorityOrder[b.priority] || 1;

      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }

      // If same priority, sort by expected date (most recent first)
      const aDate = dayjs(a.expectedDate);
      const bDate = dayjs(b.expectedDate);
      return bDate.diff(aDate);
    });

    // Timeline view with collision detection - items are placed horizontally, pushed down only when colliding
    const cardHeight = 250; // Fixed height for timeline items
    const cardMargin = 10; // Margin between items

    const positionedItems = [];

    // Process items one by one to handle collision detection properly
    itemsWithPositions.forEach((item) => {
      // Extract numeric values from pixel strings (e.g., "140px" -> 140)
      const startPixels = parseInt(item.position.left.replace("px", ""));
      const widthPixels = parseInt(item.position.width.replace("px", ""));
      const endPixels = startPixels + widthPixels;

      // Find the first available lane for this item
      let laneIndex = 0;
      let foundLane = false;

      while (!foundLane) {
        // Check if this lane is available for the entire duration of the item
        let laneAvailable = true;

        // Check if any existing item in this lane overlaps with this item
        for (let existingItem of positionedItems) {
          if (existingItem.laneIndex === laneIndex) {
            const existingStart = parseInt(
              existingItem.position.left.replace("px", "")
            );
            const existingWidth = parseInt(
              existingItem.position.width.replace("px", "")
            );
            const existingEnd = existingStart + existingWidth;

            // Check for overlap: if start < existingEnd AND end > existingStart
            if (startPixels < existingEnd && endPixels > existingStart) {
              laneAvailable = false;
              break;
            }
          }
        }

        if (laneAvailable) {
          foundLane = true;
        } else {
          laneIndex++;
        }
      }

      const yPosition = laneIndex * (cardHeight + cardMargin);

      positionedItems.push({
        ...item,
        yPosition,
        laneIndex,
      });
    });

    // Calculate container height based on the highest lane used
    const maxLaneIndex = Math.max(
      ...positionedItems.map((item) => item.laneIndex),
      0
    );
    const containerHeight = (maxLaneIndex + 1) * (cardHeight + cardMargin) + 10;

    return {
      items: positionedItems,
      containerHeight,
    };
  };

  // Group data by user and priority
  const groupedData = useMemo(() => {
    const groups = {};
    const timelineStart =
      timeline[0]?.weekStart || dayjs().startOf("month").startOf("week");
    const timelineEnd =
      timeline[timeline.length - 1]?.weekEnd ||
      dayjs().add(1, "month").endOf("month").endOf("week");

    data.forEach((item) => {
      const user = item.assignedTo || "Unassigned";
      const itemStart = dayjs(item.createdDate);
      const itemEnd = dayjs(item.expectedDate);
      const itemCompleted = isCompleted(item);

      if (itemCompleted) {
        const itemInTimeframe =
          itemStart.isBefore(timelineEnd) && itemEnd.isAfter(timelineStart);
        if (!itemInTimeframe) {
          return;
        }
      }

      if (!groups[user]) {
        groups[user] = {
          rocks: [],
          urgent: [],
          medium: [],
          normal: [],
        };
      }

      let priorityGroup = "normal";
      const priorityCategory = item.priority || "Normal";

      // Check if this is a ROCK item (subject starts with "ROCK:")
      const subject = item.name || item.description || item.subject || "";
      if (subject.startsWith("ROCK:")) {
        priorityGroup = "rocks";
      } else if (type === "website") {
        if (priorityCategory === "Urgent") {
          priorityGroup = "urgent";
        } else if (
          priorityCategory === "High" ||
          priorityCategory === "Medium-High"
        ) {
          priorityGroup = "medium";
        } else if (
          priorityCategory === "Medium" ||
          priorityCategory === "Low"
        ) {
          priorityGroup = "normal";
        }
      } else {
        if (priorityCategory === "Urgent") {
          priorityGroup = "urgent";
        } else if (priorityCategory === "Medium") {
          priorityGroup = "medium";
        }
      }

      groups[user][priorityGroup].push(item);
    });

    return groups;
  }, [data, timeline, type]);

  // Filter by selected user if specified
  const filteredGroups = useMemo(() => {
    if (!selectedUser || selectedUser === "All") {
      return groupedData;
    }

    return selectedUser in groupedData
      ? { [selectedUser]: groupedData[selectedUser] }
      : {};
  }, [groupedData, selectedUser]);

  // Helper function to get priority style
  const getPriorityStyle = (priority, isCompleted) => {
    let backgroundColor = "#10b981";
    let borderColor = "#059669";

    switch (priority) {
      case "ROCKS":
        backgroundColor = isCompleted ? "#ffcdd2" : "#d32f2f";
        borderColor = "#b71c1c";
        break;
      case "Urgent":
        backgroundColor = isCompleted ? "#ffcdd2" : "#e53935";
        borderColor = "#c62828";
        break;
      case "High":
      case "Medium-High":
        backgroundColor = isCompleted ? "#fff3e0" : "#fb8c00";
        borderColor = "#f57c00";
        break;
      case "Medium":
        backgroundColor = isCompleted ? "#fff3e0" : "#fb8c00";
        borderColor = "#f57c00";
        break;
      default:
        backgroundColor = isCompleted ? "#e8f5e8" : "#10b981";
        borderColor = "#059669";
        break;
    }

    return {
      backgroundColor,
      borderColor,
      color: isCompleted ? "#666" : "#fff",
    };
  };

  // Helper function to get priority icon
  const getPriorityIcon = (priority) => {
    if (priority === "ROCKS") return "💎";
    if (priority === "Urgent") return "🔥";
    if (priority === "High" || priority === "Medium-High") return "⚡";
    if (priority === "Medium") return "📋";
    if (priority === "Low") return "📝";
    return "📋";
  };

  // Render priority group
  const renderPriorityGroup = (items, priorityName, priorityIcon, user) => {
    if (items.length === 0) return null;

    const stackingResult = calculateStackPositions(items);

    return (
      <div className={`gantt-priority-group ${priorityName.toLowerCase()}`}>
        <div className="priority-label">
          <span className="priority-icon">{priorityIcon}</span>
          <span>
            {priorityName} ({items.length})
          </span>
        </div>
        <div className="gantt-user-items">
          <div
            className="gantt-stacked-container"
            style={{
              height: `${stackingResult.containerHeight}px`,
              position: "relative",
              minHeight: "50px",
              width: `${timeline.length * 140}px`, // Fixed width to match timeline
            }}
          >
            {stackingResult.items.map((item) => {
              // For ROCKS items, use ROCKS priority for styling
              const itemPriority =
                priorityName === "ROCKS" ? "ROCKS" : item.priority;
              const style = getPriorityStyle(itemPriority, isCompleted(item));
              const completed = isCompleted(item);

              return (
                <div
                  key={item.id}
                  className={`gantt-item ${priorityName.toLowerCase()} ${
                    completed ? "completed" : ""
                  }`}
                  style={{
                    position: "absolute",
                    left: item.position.left,
                    width: item.position.width,
                    top: `${item.yPosition}px`,
                    height: "250px",
                    backgroundColor: style.backgroundColor,
                    borderColor: style.borderColor,
                    color: style.color,
                    cursor: "pointer",
                    borderRadius: "6px",
                    border: "2px solid",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    minWidth: "120px",
                    fontWeight: "600",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "all 0.2s ease",
                  }}
                  title={`Click to open in Salesforce: ${
                    item.name || item.description || item.subject
                  } - ${item.priority} - ${user}`}
                  onClick={() => openSalesforceRecord(item.id)}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                      height: "100%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        lineHeight: "1.3",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        marginBottom: "8px",
                        flex: 1,
                        maxHeight: "60px",
                      }}
                    >
                      {getPriorityIcon(itemPriority)}{" "}
                      {item.name ||
                        item.description ||
                        item.subject ||
                        `Item ${item.id}`}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        opacity: "0.8",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      📅 {dayjs(item.createdDate).format("MMM D, YYYY")}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        opacity: "0.8",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      ⏰ Due: {dayjs(item.expectedDate).format("MMM D, YYYY")}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        opacity: "0.7",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        fontWeight: "500",
                        marginTop: "auto",
                        padding: "8px",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                        textAlign: "center",
                      }}
                    >
                      Duration: {item.position.durationDays} day
                      {item.position.durationDays > 1 ? "s" : ""}
                    </span>
                  </div>
                  {completed && (
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        marginLeft: "4px",
                        opacity: 0.9,
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="gantt-container gantt-timeline-view">
      <div className="gantt-header">
        <div className="gantt-timeline">
          {timeline.map((week, weekIndex) => (
            <div key={weekIndex} className="gantt-week">
              <div className="week-header">
                <span className="week-dates">
                  {week.weekStart.format("MMM D")} - {week.weekEnd.format("D")}
                </span>
              </div>
              <div className="gantt-days">
                {week.days.map((day, dayIndex) => (
                  <div
                    key={dayIndex}
                    className={`gantt-day ${
                      day.isSame(dayjs(), "day") ? "today" : ""
                    }`}
                  >
                    <div className="day-number">{day.format("D")}</div>
                    <div className="day-name">{day.format("ddd")[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="gantt-body">
        {Object.entries(filteredGroups).map(([user, userGroups]) => {
          const totalItems =
            userGroups.rocks.length +
            userGroups.urgent.length +
            userGroups.medium.length +
            userGroups.normal.length;

          if (totalItems === 0) return null;

          return (
            <div key={user} className="gantt-user-section">
              <div className="gantt-user-header">
                <h4>{user}</h4>
                <span className="item-count">
                  ({totalItems} {type})
                </span>
              </div>

              {renderPriorityGroup(userGroups.rocks, "ROCKS", "💎", user)}
              {renderPriorityGroup(userGroups.urgent, "Urgent", "🔥", user)}
              {renderPriorityGroup(userGroups.medium, "Medium", "⚡", user)}
              {renderPriorityGroup(userGroups.normal, "Normal", "📋", user)}
            </div>
          );
        })}

        {Object.keys(filteredGroups).length === 0 && (
          <div className="gantt-empty">
            <p>No {type} found for the selected user.</p>
          </div>
        )}
      </div>

      <div className="gantt-legend">
        <div className="legend-item">
          <div className="legend-color rocks"></div>
          <span>💎 ROCKS</span>
        </div>
        <div className="legend-item">
          <div className="legend-color urgent"></div>
          <span>🔥 Urgent Priority</span>
        </div>
        <div className="legend-item">
          <div className="legend-color medium"></div>
          <span>⚡ Medium Priority</span>
        </div>
        <div className="legend-item">
          <div className="legend-color normal"></div>
          <span>📋 Normal Priority</span>
        </div>
        <div className="legend-item">
          <div className="legend-color completed"></div>
          <span>✓ Completed</span>
        </div>
        <div className="legend-note">
          <small>
            📅 Timeline View: Items placed horizontally with collision detection
            | Current Month + Next Month
          </small>
        </div>
      </div>
    </div>
  );
};

export default GanttView;
